import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './shadcn/button';
import { Card, CardContent } from './shadcn/card';
import {
  Shield,
  Clock,
  Lock,
  CheckCircle,
  ArrowRight,
  Snowflake,
  HardDrive,
  Zap,
  Video,
  Scale,
  EyeOff,
  ZoomIn,
  X
} from 'lucide-react';

function OurHardware() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Scale,
      title: "Canadian Privacy Compliance",
      description: "Engineered around PIPEDA and provincial privacy legislation. Privacy-by-design architecture with data minimization, configurable retention, and no biometric identification."
    },
    {
      icon: Clock,
      title: "7-Day Continuous Recording",
      description: "Record a full week of uninterrupted footage on a single deployment. Intelligent loop recording ensures no moment is lost between collection cycles."
    },
    {
      icon: Video,
      title: "Traffic-Grade Imaging",
      description: "Purpose-built optics and frame timing tuned for vehicle movement analysis at intersections, corridors, and roundabouts."
    },
    {
      icon: Snowflake,
      title: "Built for Canadian Conditions",
      description: "All-weather enclosure rated for coast-to-coast deployments, built to keep recording through prairie winters and coastal humidity alike."
    },
    {
      icon: HardDrive,
      title: "Encrypted On-Device Storage",
      description: "Footage is stored locally with enterprise-grade encryption. Your data stays in your hands, with secure export when you choose."
    },
    {
      icon: Zap,
      title: "Seamless NeuralVision Integration",
      description: "Footage feeds directly into the NeuralVision TMC platform for automated Traffic Movement Count analysis, with no manual prep required."
    }
  ];

  const gallery = [
    {
      src: "/images/hardware/owl-deployed.jpg",
      alt: "Neural Vision Owl strapped to a signal pole at a live intersection",
      caption: "Strapped to an existing signal pole at a live intersection. Deployment takes minutes, not days.",
      pos: "50% 62%"
    },
    {
      src: "/images/hardware/owl-camera.jpg",
      alt: "Weather-shielded camera head of the Neural Vision Owl",
      caption: "The weather-shielded camera head, shaded for consistent footage in glare and rain.",
      pos: "center"
    },
    {
      src: "/images/hardware/owl-extended.jpg",
      alt: "Neural Vision Owl telescoping mast extended to full height at a deployment site",
      caption: "The telescoping mast extends to the height your site demands, from curb-level up to full intersection coverage.",
      pos: "50% 10%"
    },
    {
      src: "/images/hardware/owl-console.jpg",
      alt: "Neural Vision Owl on-device scheduling console",
      caption: "On-device scheduling console. Set recording windows on site, with no cloud connection required.",
      pos: "center"
    }
  ];

  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const included = [
    "Neural Vision Owl recording unit with traffic-grade optics",
    "Telescoping mast, mounting hardware, and installation kit",
    "7-day continuous loop recording, pre-configured",
    "Privacy compliance documentation and signage templates",
    "Secure export pipeline to the NeuralVision TMC platform",
    "Dedicated onboarding and 24/7 technical support"
  ];

  const compliancePoints = [
    { icon: Shield, text: "Designed to align with PIPEDA and applicable provincial privacy legislation" },
    { icon: EyeOff, text: "No facial recognition or biometric identification capabilities" },
    { icon: Lock, text: "Encrypted storage with configurable, automatic retention policies" },
    { icon: CheckCircle, text: "Documentation to support your organization's privacy impact assessments" }
  ];

  return (
    <div className="our-hardware-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-10 right-10 w-64 h-64 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-10 left-1/2 w-64 h-64 bg-indigo-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in-up">
              Our Hardware
            </h1>
            <p className="text-xl mb-8 text-blue-100 animate-fade-in-up animation-delay-200">
              Introducing the Neural Vision Owl, our purpose-built camera recording system for traffic systems.
              A full week of continuous, privacy-compliant footage, engineered for Canadian roads and regulations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-400">
              <Button
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-white text-blue-600 hover:bg-blue-50 transform hover:-translate-y-1 transition duration-300"
              >
                Contact Sales
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/pricing')}
                className="border-white text-white hover:bg-white/10 transform hover:-translate-y-1 transition duration-300"
              >
                View Pricing
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Product Showcase Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="animate-fade-in-up">
                <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden">
                  <img
                    src="/images/hardware/owl-mast.jpg"
                    alt="The Neural Vision Owl traffic recording system deployed curbside"
                    className="w-full h-96 md:h-[480px] object-cover object-top"
                  />
                </Card>
              </div>
              <div className="animate-fade-in-up animation-delay-200">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Meet the Neural Vision Owl
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  One deployment, one full week of data. The Owl perches above any intersection on its
                  telescoping mast and records continuously for seven days straight, giving your team
                  complete coverage of weekday and weekend traffic patterns without revisiting the site.
                  Combined with the NeuralVision TMC platform, it turns raw footage into actionable
                  Traffic Movement Count reports, faster, safer, and fully compliant.
                </p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-white rounded-xl shadow-md p-4 transform hover:scale-105 transition-transform duration-300">
                    <div className="text-2xl font-bold text-blue-600">7 Days</div>
                    <div className="text-sm text-gray-500">Continuous Recording</div>
                  </div>
                  <div className="bg-white rounded-xl shadow-md p-4 transform hover:scale-105 transition-transform duration-300">
                    <div className="text-2xl font-bold text-blue-600">100%</div>
                    <div className="text-sm text-gray-500">Privacy Focused</div>
                  </div>
                  <div className="bg-white rounded-xl shadow-md p-4 transform hover:scale-105 transition-transform duration-300">
                    <div className="text-2xl font-bold text-blue-600">$5,000</div>
                    <div className="text-sm text-gray-500">CAD, All Inclusive</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Built for the Way Canada Collects Traffic Data
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Every detail of the Neural Vision Owl is designed for municipal and provincial traffic programs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="group">
                  <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full transform hover:-translate-y-2 bg-white rounded-xl">
                    <CardContent className="p-8 text-center">
                      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 rounded-xl w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:from-blue-600 group-hover:to-indigo-700 transition-all duration-300">
                        <Icon className="h-10 w-10 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold mb-4 text-gray-900">{feature.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Field Gallery Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              See the Owl in the Field
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real deployments on real Canadian roads, from quick curbside setups to full intersection coverage
            </p>
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {gallery.map((item, index) => (
              <Card
                key={index}
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden bg-white rounded-xl"
              >
                <div
                  className="relative cursor-zoom-in group/img"
                  onClick={() => setLightbox(item)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setLightbox(item);
                    }
                  }}
                  aria-label={`Enlarge image: ${item.caption}`}
                >
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-full h-80 object-cover"
                    style={{ objectPosition: item.pos }}
                  />
                  <div className="absolute inset-0 bg-blue-900/0 group-hover/img:bg-blue-900/30 transition-colors duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover/img:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3 shadow-lg">
                      <ZoomIn className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-600 leading-relaxed">{item.caption}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6 animate-fade-in-up">
                  Privacy Is a Feature, Not a Checkbox
                </h2>
                <p className="text-gray-600 mb-6 animate-fade-in-up animation-delay-200">
                  Public-space recording carries real responsibility. The Neural Vision Owl was designed
                  from the ground up to follow Canadian privacy laws, so municipalities and
                  engineering firms can collect the data they need without compromising public trust.
                </p>
                <ul className="space-y-4">
                  {compliancePoints.map((point, index) => {
                    const Icon = point.icon;
                    return (
                      <li
                        key={index}
                        className="flex items-start animate-fade-in-up"
                        style={{ animationDelay: `${300 + index * 100}ms` }}
                      >
                        <Icon className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                        <span className="text-gray-700">{point.text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-8 text-white animate-fade-in-up animation-delay-200 text-center">
                <Shield className="h-16 w-16 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-4">Compliance, Documented</h3>
                <p className="mb-6">
                  Every Owl ships with privacy documentation and signage templates,
                  so your deployment is transparent with the communities it serves.
                </p>
                <Button
                  onClick={() => navigate('/privacy-policy')}
                  className="bg-white text-blue-600 hover:bg-blue-50 w-full transition duration-300 transform hover:-translate-y-0.5"
                >
                  Read Our Privacy Policy
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              One Price. Everything Included.
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              No subscriptions, no hidden fees, just dependable hardware ready to deploy
            </p>
          </div>

          <div className="max-w-lg mx-auto">
            <Card className="border-0 shadow-xl overflow-hidden transform hover:-translate-y-2 transition-all duration-300">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-8 text-center">
                <h3 className="text-2xl font-bold mb-2">Neural Vision Owl Recording System</h3>
                <div className="flex items-end justify-center gap-2">
                  <span className="text-5xl font-bold">$5,000</span>
                  <span className="text-blue-100 mb-2">CAD / unit</span>
                </div>
              </div>
              <CardContent className="p-8 bg-white">
                <ul className="space-y-4 mb-8">
                  {included.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  size="lg"
                  onClick={() => navigate('/contact')}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
                >
                  Request a Quote
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white rounded-full mix-blend-multiply filter blur-2xl animate-float"></div>
          <div className="absolute top-1/3 right-1/4 w-48 h-48 bg-blue-300 rounded-full mix-blend-multiply filter blur-2xl animate-float animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-56 h-56 bg-indigo-300 rounded-full mix-blend-multiply filter blur-2xl animate-float animation-delay-4000"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Deploy the Owl?
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              Talk to our team about hardware deployments for your next traffic study,
              whether that's a single intersection or a province-wide program.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-white text-blue-600 hover:bg-blue-50 transform hover:-translate-y-1 transition duration-300"
              >
                Contact Sales
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/contact-us')}
                className="border-white text-white hover:bg-white/10 transform hover:-translate-y-1 transition duration-300"
              >
                Ask a Question
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.alt}
        >
          <button
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-colors"
            onClick={() => setLightbox(null)}
            aria-label="Close enlarged image"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-h-[85vh] max-w-full rounded-xl shadow-2xl"
          />
          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/90 bg-black/50 rounded-full px-5 py-2 text-sm md:text-base text-center max-w-[90vw]">
            {lightbox.caption}
          </p>
        </div>
      )}
    </div>
  );
}

export default OurHardware;
